import React, { FC, useContext } from "react";
import {
  formatCityStateZipByCountry,
  formatFullName,
  formatStreetByCountry,
} from "@pepdirect/v3/helpers/address";
import { SavedOption } from "v3/components/checkout/SavedOption";
import { SavedShippingAddress } from "v3/services/graphql/generated";
import {
  StyledSavedAddresses,
  StyledSpecialInstructions,
} from "./index.styled";
import { formatPhoneByCountry } from "@pepdirect/v3/helpers/phone";
import { TenantContext } from "v3/context/tenant";

interface SavedAddressesProps {
  addresses: SavedShippingAddress[];
  selectedAddressId: string | null;
  setSelectedAddressId: (id: string) => void;
}

export const SavedAddresses: FC<SavedAddressesProps> = ({
  addresses = [],
  selectedAddressId,
  setSelectedAddressId,
}) => {
  const { tenant } = useContext(TenantContext);
  if (!addresses || addresses.length === 0) return null;

  const showAddressNickname =
    !!tenant?.contactConfig.shippingAddressConfig.addressNickname;
  const showSpecialInstructions =
    !!tenant?.contactConfig.shippingAddressConfig.addressSpecialInstructions;

  const handleClick = (addressId: string) => () =>
    setSelectedAddressId(addressId);

  return (
    <StyledSavedAddresses>
      {addresses.map(
        ({
          addressId,
          city,
          countryCode: countryFormat,
          firstName,
          isDefault,
          lastName,
          line1,
          line2,
          nickname,
          phoneNumber,
          specialInstructions,
          state,
          zip,
        }) => {
          const fullName = formatFullName({
            firstName,
            lastName,
            countryFormat,
          });
          const phone = phoneNumber
            ? formatPhoneByCountry(phoneNumber, countryFormat)
            : "";
          const streetAddress = formatStreetByCountry({
            line1,
            line2,
            countryFormat,
          });
          const cityStateZip = formatCityStateZipByCountry({
            city,
            state,
            zip,
            countryFormat,
          });

          return (
            <SavedOption
              key={addressId}
              checked={selectedAddressId === addressId}
              handleClick={handleClick(addressId)}
              isDefault={isDefault}
            >
              {showAddressNickname && nickname && <div>{nickname}</div>}
              <div>{fullName}</div>
              <div>{streetAddress}</div>
              <div>{cityStateZip}</div>
              <div>{phone}</div>
              {showSpecialInstructions && specialInstructions && (
                <StyledSpecialInstructions>
                  {specialInstructions}
                </StyledSpecialInstructions>
              )}
            </SavedOption>
          );
        }
      )}
    </StyledSavedAddresses>
  );
};
